import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack } from "@mui/material";
import CustomLayout from "../../../components/layout";
import PrimaryButton from "../../../components/buttons/primary-button";
import SecondaryButton from "../../../components/buttons/secondary-button";
import LoanInfoCard from "../../../components/info-card";
import SubmissionChecklist from "../../../components/submission-checklist";
import CustomAlert from "../../../components/custom-alert";
import ProcessingModal from "../../../components/modal/processing-modal";
import LoaderModal from "../../../components/modal/loader-modal";
import SuccessModal from "../../../components/modal/success-modal";
import CommanErrorModal from "../../../components/modal/common-error-modal";
import PathSelectionCard from "../../../components/path-selection-card";
import AuraConditionList from "../../../components/aura-condition-list";
import Heading4 from "../../../components/typography/heading-04";
import Heading5 from "../../../components/typography/heading-05";
import Body3 from "../../../components/typography/body-03";
import {
	clearCurrentLoan,
	setLockDecision,
	setCurrentStage,
	selectCurrentStage,
	selectLoanGuid,
	selectAuraResult,
	selectSelectedProgram,
	selectLockDecision,
	selectExceptionStatus,
	selectSectionStatuses,
	selectFicoScore,
	selectProductInputs,
	selectSelectedPath,
} from "../../../features/current-loan/currentLoanSlice";
import { useSyncAuraProgress } from "../../../features/current-loan/useSyncAuraProgress";
import { useGetLoanMutation } from "../../../features/get-loan-data/getLoanApiSlice";
import { isRealLoanGuid } from "../../../utils/normalize-xml-for-create-loan";
import { formatCurrency } from "../../../utils/format-currency";
import {
	useLazyGetAuraConditionsQuery,
	useSubmitLoanMutation,
} from "../../../features/current-loan/currentLoanApiSlice";
import { countMinimumRemaining } from "../../../utils/aura-status-poll";
import { VERBIAGE_CONSTANTS } from "../../../utils/verbiage.constants";
import { useErrorMessages } from "../../../utils/error-messages.constants";
import { getConflictPopup, isConflictError } from "../../../utils/conflict-error";
import useAuraInProgress from "../../../hooks/useAuraInProgress";
import AuraInProgressBanner from "../../../components/aura-in-progress-banner";
import Body2 from "../../../components/typography/body-02";
import styles from "./index.module.scss";

const buildChecklistItems = ({
	auraResult,
	selectedProgram,
	lockDecision,
	exceptionStatus,
	sectionStatuses,
	minimumRemaining,
	ficoScore,
}) => {
	const allSectionsComplete = Object.values(sectionStatuses).every(
		(s) => s === "complete"
	);
	const needsListComplete = minimumRemaining === 0;
	// Step A stores the qualifying FICO only after a successful credit re-issue.
	const creditOnFile = !!ficoScore;
	const programRegistered = !!selectedProgram;

	return [
		{
			id: "program",
			label: "Loan program registered",
			status: programRegistered ? "pass" : "fail",
			note: programRegistered
				? `${selectedProgram.name}`
				: "Program not yet registered with Lock Desk",
		},
		{
			id: "aura",
			label: "AURA result",
			status: auraResult === "eligible" ? "pass" : auraResult ? "warn" : "fail",
			note: auraResult ? `AURA ${auraResult}` : "AURA not run",
		},
		{
			id: "credit",
			label: "Credit report on file",
			status: creditOnFile ? "pass" : "warn",
			note: creditOnFile ? "Credit re-issue on file" : "No credit report — manual review will apply",
		},
		{
			id: "application",
			label: "Application validation complete",
			status: allSectionsComplete ? "pass" : "warn",
			note: allSectionsComplete
				? "All sections complete"
				: "Some Dashboard sections need review — save each section with its Save button",
		},
		{
			id: "needs",
			label: "Minimum Submission documents linked",
			status: needsListComplete ? "pass" : "fail",
			note: needsListComplete
				? "Every Minimum Submission condition has a document"
				: minimumRemaining === null
				? "Conditions not loaded"
				: `${minimumRemaining} condition${minimumRemaining === 1 ? "" : "s"} remaining`,
		},
		{
			id: "lock",
			label: "Rate / lock decision confirmed",
			status: lockDecision ? "pass" : "fail",
			note: lockDecision === "lock"
				? "Lock request submitted"
				: lockDecision === "float"
				? "Registered & floating"
				: "No lock decision",
		},
		{
			id: "exception",
			label: "Exception status",
			status:
				exceptionStatus === "approved"
					? "pass"
					: exceptionStatus === "pending"
					? "warn"
					: exceptionStatus === "denied"
					? "fail"
					: "pass",
			note:
				exceptionStatus === "none"
					? "No exception requested"
					: `Exception ${exceptionStatus}`,
		},
	];
};

const deriveGateState = ({ auraResult, selectedProgram, exceptionStatus }) => {
	if (!selectedProgram) return "hard";
	if (
		auraResult === "ineligible" ||
		exceptionStatus === "pending" ||
		auraResult === null
	)
		return "amber";
	return "green";
};

const SubmitPage = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const loanGuid = useSelector(selectLoanGuid);
	useSyncAuraProgress();
	const currentStage = useSelector(selectCurrentStage);

	// Assert "submit" on mount regardless of navigation method — mirrors the same fix
	// in dashboard/index.jsx, qualify/aura/index.jsx, and qualify/non-aura/index.jsx.
	React.useEffect(() => {
		if (loanGuid && currentStage !== "submit") dispatch(setCurrentStage("submit"));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);

	const auraResult = useSelector(selectAuraResult);
	const selectedProgram = useSelector(selectSelectedProgram);
	const lockDecision = useSelector(selectLockDecision);
	const exceptionStatus = useSelector(selectExceptionStatus);
	const sectionStatuses = useSelector(selectSectionStatuses);
	const ficoScore = useSelector(selectFicoScore);
	const productInputs = useSelector(selectProductInputs);
	// PathC-4.01: the manual path shows Minimum Submission conditions only.
	const isAuraPath = useSelector(selectSelectedPath) === "aura";

	const [submitLoan] = useSubmitLoanMutation();
	const [getAuraConditions] = useLazyGetAuraConditionsQuery();
	const { getErrorMessage } = useErrorMessages();
	// LC-696: the loan's real documentation conditions (null until loaded).
	const [conditions, setConditions] = useState(null);
	const [conditionsError, setConditionsError] = useState(null);
	const [conditionsLoading, setConditionsLoading] = useState(false);
	// A failed load keeps the gate closed and offers Retry (Submit must not unlock unverified).
	const [conditionsLoadFailed, setConditionsLoadFailed] = useState(false);
	const [getLoan] = useGetLoanMutation();
	const [understandChecked, setUnderstandChecked] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [success, setSuccess] = useState(false);
	// Submit errors: { title, message, code } (409 → backend message; otherwise SUBMIT_FAILED).
	const [errorModal, setErrorModal] = useState(null);
	// Nothing can be submitted, chosen or uploaded while an AURA run is in flight.
	const { locked: auraLocked } = useAuraInProgress(loanGuid);
	// Property address for the pre-submission summary grid (mirrors the
	// prototype's sum-grid, which shows Property alongside the top loan strip).
	const [property, setProperty] = useState({});

	React.useEffect(() => {
		if (!loanGuid) navigate("/current-loan");
	}, [loanGuid, navigate]);

	React.useEffect(() => {
		if (!isRealLoanGuid(loanGuid)) return;
		getLoan({ loanid: loanGuid })
			.unwrap()
			.then((loanData) => setProperty(loanData?.response?.property || {}))
			.catch(() => {});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);

	const loadConditions = async () => {
		if (!isRealLoanGuid(loanGuid)) return;
		setConditionsLoading(true);
		try {
			setConditions(await getAuraConditions(loanGuid).unwrap());
			setConditionsLoadFailed(false);
		} catch (err) {
			setConditionsLoadFailed(true);
			setConditionsError(getErrorMessage("AURA_CONDITIONS_ERROR"));
		} finally {
			setConditionsLoading(false);
		}
	};

	React.useEffect(() => {
		loadConditions();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);

	// PathA-6.06 / PathC-4.03: every Minimum Submission condition needs a document.
	const minimumRemaining = conditions ? countMinimumRemaining(conditions) : null;
	const minimumMet = minimumRemaining === 0;
	const preliminaryOpen = isAuraPath
		? (conditions?.preliminaryCredit || []).filter((c) => !(Number(c?.docsCount) > 0)).length
		: 0;

	const checklistItems = buildChecklistItems({
		auraResult,
		selectedProgram,
		lockDecision,
		exceptionStatus,
		sectionStatuses,
		minimumRemaining,
		ficoScore,
	});

	const gateState = deriveGateState({ auraResult, selectedProgram, exceptionStatus });

	const canSubmit =
		!auraLocked &&
		gateState !== "hard" &&
		minimumMet &&
		!!lockDecision &&
		(gateState !== "amber" || understandChecked);

	const handleSubmit = async () => {
		setProcessing(true);
		try {
			await submitLoan(loanGuid).unwrap();
			setSuccess(true);
		} catch (err) {
			setErrorModal(
				isConflictError(err) ? getConflictPopup(err, getErrorMessage) : getErrorMessage("SUBMIT_FAILED")
			);
		} finally {
			setProcessing(false);
		}
	};

	const handleSuccessClose = () => {
		dispatch(clearCurrentLoan());
		navigate(`/loan-summary/${loanGuid}`);
	};

	return (
		<CustomLayout>
			<ProcessingModal open={processing} text="Submitting your loan application…" />
			<SuccessModal
				open={success}
				handleClose={handleSuccessClose}
				text={
					lockDecision === "lock"
						? "Loan submitted to Logan with a lock request. You will be notified of next steps."
						: "Loan submitted to Logan and will continue to float. You will be notified of next steps."
				}
				confirmText="View loan summary →"
			>
				<Stack direction="column" gap={1} mb={2}>
					<Body3
						text={
							!isAuraPath
								? "Eligibility will be reviewed manually by an underwriter."
								: preliminaryOpen > 0
								? `${preliminaryOpen} Preliminary Credit condition${preliminaryOpen === 1 ? "" : "s"} still open. You can finish ${preliminaryOpen === 1 ? "it" : "them"} anytime in the Docs & Condition Tracker.`
								: "All conditions have documents linked. Your file is ready for review."
						}
						extraClass={styles.subTitle}
					/>
					<Body3 text="Loan status: Submitted to Logan · TPO.X90 set" extraClass={styles.subTitle} />
				</Stack>
			</SuccessModal>
			<CommanErrorModal
				isOpen={!!errorModal}
				onClose={() => setErrorModal(null)}
				title={errorModal?.title}
				message={errorModal?.message}
				errorCode={errorModal?.code}
			/>
			<LoaderModal open={conditionsLoading} />
			<CommanErrorModal
				isOpen={!!conditionsError}
				onClose={() => setConditionsError(null)}
				title={conditionsError?.title}
				message={conditionsError?.message}
				errorCode={conditionsError?.code}
			/>

			<Box className={styles.pageRoot}>
				<LoanInfoCard loanId={loanGuid} />
				<AuraInProgressBanner show={auraLocked} />
				<Stack direction="column" mb={3}>
					<Heading4 text="Step 4: Submit" fontType="semibold" />
					<Body3
						text="Review all readiness conditions before submitting the loan."
						extraClass={styles.subTitle}
					/>
				</Stack>

				{gateState === "hard" && (
					<CustomAlert
						subject="Submission blocked"
						details="This loan cannot be submitted until a program has been registered in Step 3 (Qualify)."
						severity="error"
						extraClass={styles.alertSpacing}
					/>
				)}

				{gateState === "amber" && (
					<CustomAlert
						subject="Open items detected"
						details="This loan has open items. You may submit, but it will route to manual underwriter review. Check the 'I understand' box below to confirm."
						severity="warning"
						extraClass={styles.alertSpacing}
					/>
				)}

				{gateState === "green" && (
					<CustomAlert
						subject="Ready for submission"
						details="All required conditions are met. This loan may qualify for standard processing."
						severity="success"
						extraClass={styles.alertSpacing}
					/>
				)}

				<Box className={styles.presubmitCard}>
					<Heading4 text="Pre-submission summary" fontType="semibold" extraClass={styles.presubmitTitle} />
					<Box className={styles.presubmitGrid}>
						<Stack direction="column" gap={0.5}>
							<Body3 text="Program" extraClass={styles.subTitle} />
							<Body2 text={selectedProgram?.name || "Not selected"} fontType="semibold" />
						</Stack>
						<Stack direction="column" gap={0.5}>
							<Body3 text="Rate / Price" extraClass={styles.subTitle} />
							<Body2
								text={
									selectedProgram?.rate != null || selectedProgram?.price != null
										? `${selectedProgram?.rate != null ? Number(selectedProgram.rate).toFixed(3) + "%" : "—"} / ${
											selectedProgram?.price != null ? Number(selectedProgram.price).toFixed(3) : "—"
										}`
										: "Not selected"
								}
								fontType="semibold"
							/>
						</Stack>
						<Stack direction="column" gap={0.5}>
							<Body3 text="Loan amount" extraClass={styles.subTitle} />
							<Body2
								text={
									productInputs?.loanAmount
										? formatCurrency.format(Number(productInputs.loanAmount) || 0)
										: "—"
								}
								fontType="semibold"
							/>
						</Stack>
						<Stack direction="column" gap={0.5}>
							<Body3 text="Property" extraClass={styles.subTitle} />
							<Body2
								text={
									[property?.addressLineText, property?.city, property?.state]
										.filter(Boolean)
										.join(", ") || "—"
								}
								fontType="semibold"
							/>
						</Stack>
						<Stack direction="column" gap={0.5}>
							<Body3 text="Eligibility" extraClass={styles.subTitle} />
							<Body2
								text={
									auraResult === "eligible"
										? "Eligible (AURA)"
										: auraResult
										? "Manual review (AURA ineligible)"
										: "Manual review (no AURA)"
								}
								fontType="semibold"
							/>
						</Stack>
						<Stack direction="column" gap={0.5}>
							<Body3 text="Estimated payment" extraClass={styles.subTitle} />
							<Body2
								text={
									selectedProgram?.totalPayment != null
										? `${formatCurrency.format(Number(selectedProgram.totalPayment) || 0)}/mo`
										: "—"
								}
								fontType="semibold"
							/>
						</Stack>
					</Box>
				</Box>

				{conditionsLoadFailed && !conditionsLoading && (
					<CustomAlert
						subject={VERBIAGE_CONSTANTS.AURA_CONDITIONS_LOAD_FAILED}
						severity="warning"
						showClearButton
						clearButtonText={VERBIAGE_CONSTANTS.RETRY}
						onClearMessage={loadConditions}
						extraClass={styles.alertSpacing}
					/>
				)}

				{selectedProgram && (
					<Stack direction="column" mb={3}>
						<Heading4 text="Outstanding pre-submission tasks" fontType="semibold" extraClass={styles.presubmitTitle} />
						<AuraConditionList
							conditions={conditions}
							loanGuid={loanGuid}
							showPreliminary={isAuraPath}
							onRefresh={loadConditions}
							onError={(key) => setConditionsError(getErrorMessage(key))}
							disabled={auraLocked}
						/>
						{minimumRemaining > 0 && (
							<CustomAlert
								subject={VERBIAGE_CONSTANTS.MIN_SUBMISSION_REMAINING(minimumRemaining)}
								severity="warning"
								extraClass={styles.alertSpacing}
							/>
						)}
					</Stack>
				)}

				{selectedProgram && (
					<Stack direction="column" mb={3}>
						<Heading5 text="How would you like to submit?" extraClass={styles.presubmitTitle} />
						<Stack direction="row" gap={3} className={styles.submitChoiceRow}>
							<PathSelectionCard
								selected={lockDecision === "float"}
								disabled={!minimumMet || auraLocked}
								onSelect={() => dispatch(setLockDecision("float"))}
								badge="No rate lock yet"
								badgeColor="grey"
								title="Float & Submit Loan"
								description="Submit the loan to Logan and continue to float your registration. You can request a lock later."
								steps={[]}
							/>
							<PathSelectionCard
								selected={lockDecision === "lock"}
								disabled={!minimumMet || auraLocked}
								onSelect={() => dispatch(setLockDecision("lock"))}
								badge="Rate locked now"
								badgeColor="green"
								title="Request Lock & Submit Loan"
								description="Submit the loan and simultaneously send a lock request to Optimal Blue at the selected price."
								steps={[]}
							/>
						</Stack>
					</Stack>
				)}

				<SubmissionChecklist
					items={checklistItems}
					gateState={gateState}
					understandChecked={understandChecked}
					onUnderstandChange={setUnderstandChecked}
				/>

				<Stack
					direction="row"
					alignItems="center"
					justifyContent="flex-end"
					gap={1.5}
					mt={3}
					className={styles.actionBar}
				>
					<SecondaryButton text="Back to Dashboard" onClick={() => navigate("/current-loan/dashboard")} />
					<PrimaryButton
						text={gateState === "amber" ? "Submit loan (with exceptions) →" : "Submit loan →"}
						onClick={handleSubmit}
						disabled={!canSubmit}
						extraClass={gateState === "amber" ? styles.amberBtn : ""}
					/>
				</Stack>

				<Box className={styles.disclaimer}>
					Not a commitment to lend. All loans subject to underwriting approval.
				</Box>
			</Box>
		</CustomLayout>
	);
};

export default SubmitPage;
